import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Search, X } from "lucide-react";
import { InlineLoadError } from "@/components/feedback/InlineLoadError";
import { OperationalSelect } from "@/components/operational/OperationalSelect";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import {
  useQuotationAssignmentIds,
  useQuotationAssignments,
  useReplaceQuotationAssignments,
  quotationKeys,
} from "@/features/quotation/hooks";
import type {
  DailyQuotation,
  QuotationAssignmentFilter,
} from "@/features/quotation/types";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { formatDateBR } from "@/lib/timeAgo";
import { useDialogFocus } from "@/hooks/useDialogFocus";
import {
  getAssignmentInitialization,
  isCurrentQuotationOperation,
} from "@/features/quotation/publication";
import "./quotation-assignment.css";

const LIMIT = 8;

export function QuotationAssignmentDialog({
  quotation,
  purpose = "manage",
  onClose,
}: {
  quotation: DailyQuotation | null;
  purpose?: "manage" | "complete";
  onClose: () => void;
}) {
  const quotationId = quotation?.id ?? "";
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search.trim(), 250);
  const [assignment, setAssignment] =
    useState<QuotationAssignmentFilter>("all");
  const [offset, setOffset] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedResult, setSelectedResult] = useState<{
    key: string;
    ids: string[];
  } | null>(null);
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLElement>(null);
  const opening = useRef(0);
  const operation = useRef(0);
  const activeQuotationId = useRef(quotationId);
  activeQuotationId.current = quotationId;
  const fetchingOpening = useRef(0);
  const initializedOpening = useRef(0);
  const previousQuotationId = useRef("");
  const dirty = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const query = useQuotationAssignments(
    quotationId,
    { limit: LIMIT, offset, search: debouncedSearch, assignment },
    Boolean(quotation),
  );
  const allIdsMutation = useQuotationAssignmentIds();
  const replaceMutation = useReplaceQuotationAssignments(quotationId);
  const total = query.data?.total ?? 0;
  const correctingPage = Boolean(
    quotation &&
    query.isSuccess &&
    !query.isPlaceholderData &&
    !query.isFetching &&
    query.data?.offset === offset &&
    offset > 0 &&
    offset >= total,
  );
  const rows = correctingPage ? [] : (query.data?.items ?? []);
  const initialized =
    opening.current > 0 && initializedOpening.current === opening.current;
  const searchSettled = search.trim() === debouncedSearch;
  const mutationPending = allIdsMutation.isPending || replaceMutation.isPending;
  const saveDisabled = !initialized || mutationPending;

  useEffect(() => setOffset(0), [debouncedSearch, assignment]);

  useEffect(() => {
    if (!correctingPage) return;
    const nextOffset = Math.max(0, Math.ceil(total / LIMIT) - 1) * LIMIT;
    queryClient.removeQueries({
      queryKey: quotationKeys.assignments(quotationId, {
        limit: LIMIT,
        offset: nextOffset,
        search: debouncedSearch,
        assignment,
      }),
      exact: true,
    });
    setOffset(nextOffset);
  }, [
    correctingPage,
    total,
    quotationId,
    debouncedSearch,
    assignment,
    queryClient,
  ]);

  useEffect(() => {
    if (!quotation || previousQuotationId.current === quotation.id) return;
    previousQuotationId.current = quotation.id;
    opening.current += 1;
    operation.current += 1;
    initializedOpening.current = 0;
    dirty.current = false;
    setSelectedIds(new Set());
    setSelectedResult(null);
    setError(null);
    void query.refetch();
  }, [quotation]);

  useEffect(() => {
    if (!quotation) return;
    if (query.isFetching) {
      fetchingOpening.current = opening.current;
      return;
    }
    if (!query.isSuccess || !query.data) return;
    const initial = getAssignmentInitialization({
      opening: opening.current,
      activeOpening: fetchingOpening.current,
      initializedOpening: initializedOpening.current,
      dirty: dirty.current,
      assignedClientIds: query.data.assigned_client_ids,
      successful: true,
    });
    if (!initial) return;
    initializedOpening.current = opening.current;
    setSelectedIds(initial);
  }, [quotation, query.data, query.isFetching, query.isSuccess]);

  useEffect(() => {
    if (quotation) return;
    opening.current += 1;
    operation.current += 1;
    previousQuotationId.current = "";
    initializedOpening.current = 0;
    dirty.current = false;
    setSearch("");
    setAssignment("all");
    setOffset(0);
    setSelectedResult(null);
    setError(null);
  }, [quotation]);

  function toggle(id: string) {
    dirty.current = true;
    setError(null);
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function selectAllResults() {
    const activeOpening = opening.current;
    const activeOperation = ++operation.current;
    const activeId = quotationId;
    dirty.current = true;
    setError(null);
    try {
      const result = await allIdsMutation.mutateAsync({
        quotationId,
        search: debouncedSearch,
        assignment,
      });
      if (
        activeOpening === opening.current &&
        isCurrentQuotationOperation({
          quotationId: activeId,
          activeQuotationId: activeQuotationId.current,
          generation: activeOperation,
          activeGeneration: operation.current,
        })
      ) {
        setSelectedIds(
          (current) => new Set([...current, ...result.client_ids]),
        );
        setSelectedResult({
          key: `${activeId}:${debouncedSearch}:${assignment}`,
          ids: result.client_ids,
        });
      }
    } catch (cause) {
      if (
        !isCurrentQuotationOperation({
          quotationId: activeId,
          activeQuotationId: activeQuotationId.current,
          generation: activeOperation,
          activeGeneration: operation.current,
        })
      )
        return;
      setError(
        getUserFacingErrorMessage(
          cause,
          "Não foi possível selecionar todos os clientes.",
        ),
      );
    }
  }

  async function save() {
    setError(null);
    if (purpose === "complete" && selectedIds.size === 0) {
      setError("Selecione pelo menos um cliente para concluir a cotação.");
      return;
    }
    const activeOperation = ++operation.current;
    const activeId = quotationId;
    try {
      await replaceMutation.mutateAsync(Array.from(selectedIds).sort());
      if (
        !isCurrentQuotationOperation({
          quotationId: activeId,
          activeQuotationId: activeQuotationId.current,
          generation: activeOperation,
          activeGeneration: operation.current,
        })
      )
        return;
      operation.current += 1;
      onClose();
    } catch (cause) {
      if (
        !isCurrentQuotationOperation({
          quotationId: activeId,
          activeQuotationId: activeQuotationId.current,
          generation: activeOperation,
          activeGeneration: operation.current,
        })
      )
        return;
      setError(
        getUserFacingErrorMessage(
          cause,
          "Não foi possível salvar os clientes ativos.",
        ),
      );
    }
  }

  const visibleSelected = rows.filter((row) => selectedIds.has(row.client_id));
  const allVisibleSelected =
    rows.length > 0 && visibleSelected.length === rows.length;
  const allResultsSelected =
    total > 0 &&
    searchSettled &&
    !query.isFetching &&
    !query.isPlaceholderData &&
    allVisibleSelected &&
    (rows.length === total ||
      (!debouncedSearch &&
        assignment === "all" &&
        selectedIds.size === total) ||
      (selectedResult?.key ===
        `${quotationId}:${debouncedSearch}:${assignment}` &&
        selectedResult.ids.length === total &&
        selectedResult.ids.every((id) => selectedIds.has(id))));
  const close = () => {
    if (mutationPending) return;
    operation.current += 1;
    onClose();
  };
  useDialogFocus(dialogRef, close, { enabled: Boolean(quotation) });

  if (!quotation || typeof document === "undefined") return null;
  const page = Math.floor(offset / LIMIT) + 1;
  const pages = Math.max(1, Math.ceil(total / LIMIT));

  return createPortal(
    <div
      className="lnk-sheet-scrim lnk-management-modal-scrim is-open quotation-assignment-scrim"
      onClick={close}
    >
      <section
        ref={dialogRef}
        className="lnk-management-modal lnk-assignment-modal"
        role="dialog"
        aria-modal="true"
        aria-label={
          purpose === "complete" ? "Concluir cotação" : "Clientes ativos"
        }
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <span className="lnk-kicker">PUBLICAÇÃO POR CLIENTE</span>
            <h2>{formatDateBR(quotation.date)}</h2>
          </div>
          <button
            type="button"
            className="lnk-modal-close"
            aria-label="Fechar"
            onClick={close}
            disabled={mutationPending}
          >
            <X size={18} />
          </button>
        </header>
        {purpose === "complete" ? (
          <p>Confirme os clientes que receberão esta cotação.</p>
        ) : null}
        <div className="lnk-assignment-summary" aria-live="polite">
          <span>
            <strong>{selectedIds.size}</strong> selecionados
          </span>
          <span>
            <strong>{total}</strong> clientes
          </span>
        </div>
        <div className="lnk-assignment-tools">
          <label>
            <Search size={17} />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar cliente"
              aria-label="Buscar cliente"
            />
          </label>
          <OperationalSelect
            ariaLabel="Filtrar atribuição"
            value={assignment}
            menuLayer="dialog"
            onChange={(value) =>
              setAssignment(value as QuotationAssignmentFilter)
            }
            options={[
              { value: "all", label: "Todos" },
              { value: "assigned", label: "Selecionados" },
              { value: "unassigned", label: "Não selecionados" },
            ]}
          />
          <button
            type="button"
            aria-label={
              allResultsSelected
                ? "Limpar seleção"
                : "Selecionar todos os resultados"
            }
            onClick={() => {
              if (allResultsSelected) {
                dirty.current = true;
                setError(null);
                setSelectedIds(new Set());
                setSelectedResult(null);
              } else void selectAllResults();
            }}
            disabled={
              !initialized ||
              !searchSettled ||
              query.isFetching ||
              mutationPending
            }
          >
            {allResultsSelected ? "Limpar seleção" : "Selecionar todos"}
          </button>
        </div>
        <div className="lnk-client-selection-list">
          {query.isError ? (
            <InlineLoadError
              title="Clientes indisponíveis"
              message={getUserFacingErrorMessage(
                query.error,
                "Não foi possível carregar os clientes.",
              )}
              onRetry={() => void query.refetch()}
              isRetrying={query.isFetching}
            />
          ) : null}
          {rows.map((row) => {
            const selected = selectedIds.has(row.client_id);
            return (
              <button
                key={row.client_id}
                type="button"
                className={selected ? "is-selected" : ""}
                aria-pressed={selected}
                disabled={!initialized || mutationPending}
                onClick={() => toggle(row.client_id)}
              >
                <span>
                  <strong>{row.client_name}</strong>
                  <small>
                    {selected ? "Receberá esta cotação" : "Não selecionado"}
                  </small>
                </span>
                <i aria-hidden="true">
                  {selected ? <CheckCircle2 size={18} /> : null}
                </i>
              </button>
            );
          })}
          {query.isFetching && query.data ? (
            <p role="status">Atualizando clientes...</p>
          ) : null}
        </div>
        <div
          className="lnk-assignment-pager"
          aria-label="Paginação de clientes"
        >
          <div>
            <button
              type="button"
              disabled={page === 1 || query.isFetching || correctingPage}
              onClick={() => setOffset((page - 2) * LIMIT)}
            >
              Anterior
            </button>
            <span className="lnk-page-number-wrap">
              <button
                type="button"
                aria-label={`Página ${page} de ${pages}`}
                aria-current="page"
                className="current"
                disabled={query.isFetching || correctingPage}
              >
                {page} / {pages}
              </button>
            </span>
            <button
              type="button"
              disabled={page === pages || query.isFetching || correctingPage}
              onClick={() => setOffset(page * LIMIT)}
            >
              Próxima
            </button>
          </div>
        </div>
        {error ? (
          <p className="quotation-dialog-error" role="alert">
            {error}
          </p>
        ) : null}
        <footer>
          <button type="button" onClick={close} disabled={mutationPending}>
            Cancelar
          </button>
          <button
            type="button"
            className="primary"
            onClick={save}
            disabled={saveDisabled}
          >
            {purpose === "complete" ? "Concluir e publicar" : "Salvar"}
          </button>
        </footer>
      </section>
    </div>,
    document.body,
  );
}
